import { CartAddEvent, CartErrorEvent, CartGroupedSections, ThemeEvents } from "@theme/events";

let buildGalleryCartItems;
let eligibleSizes;
let FINISHES;
let PRINT_ONLY;
let variantFor;

function money(cents, currency) {
  if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents, window.FoxTheme?.moneyFormat);
  try {
    return new Intl.NumberFormat(document.documentElement.lang || "en-GB", {
      style: "currency",
      currency: currency || window.Shopify?.currency?.active || "GBP",
    }).format(Number(cents) / 100);
  } catch {
    return `${(Number(cents) / 100).toFixed(2)} ${currency || "GBP"}`;
  }
}

function instanceId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `gw-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

class SolisGalleryWall extends HTMLElement {
  async connectedCallback() {
    if (this.initialised) return;
    this.initialised = true;
    try {
      const core = await import(this.dataset.coreUrl);
      ({ buildGalleryCartItems, eligibleSizes, FINISHES, PRINT_ONLY, variantFor } = core);
    } catch (error) {
      const status = this.querySelector("[data-status]");
      if (status) {
        status.textContent = "The gallery wall configurator could not load. Please refresh the page.";
        status.dataset.error = "true";
      }
      console.error("Gallery wall core failed to load", error);
      return;
    }
    this.config = JSON.parse(this.querySelector("[data-gallery-wall-config]").textContent);
    this.items = this.config.items.map((item, index) => ({
      ...item,
      element: this.querySelectorAll("[data-gallery-item]")[index],
      product: item.product,
      size: item[`${this.config.defaultTier}Size`],
      finish: this.config.defaultFinish,
      variant: null,
    }));
    this.button = this.querySelector("[data-add-to-cart]");
    this.status = this.querySelector("[data-status]");
    this.listTotals = [...this.querySelectorAll("[data-list-price]")];
    this.discountedTotals = [...this.querySelectorAll("[data-discounted-price]")];

    this.setupItemControls();
    this.addEventListener("change", (event) => this.onChange(event));
    this.addEventListener("click", (event) => this.onClick(event));
    this.button.addEventListener("click", () => this.addToCart());
    this.resolveAll();
  }

  onClick(event) {
    const openButton = event.target.closest("[data-gallery-artwork-open]");
    if (openButton) {
      const dialog = this.querySelector(`#${CSS.escape(openButton.dataset.galleryArtworkOpen)}`);
      if (dialog?.showModal) dialog.showModal();
      return;
    }

    const closeButton = event.target.closest("[data-gallery-artwork-close]");
    if (closeButton) {
      closeButton.closest("dialog")?.close();
      return;
    }

    const dialog = event.target.closest(".gallery-wall__artwork-dialog");
    if (dialog && event.target === dialog) dialog.close();
  }

  setupItemControls() {
    for (const item of this.items) {
      const sizeSelect = item.element.querySelector("[data-item-size]");
      const finishSelect = item.element.querySelector("[data-item-finish]");
      for (const size of eligibleSizes(item.product)) sizeSelect.add(new Option(size, size));
      for (const finish of FINISHES) {
        const label = finish === PRINT_ONLY ? "Print only" : finish.replace(" frame", "");
        finishSelect.add(new Option(label, finish));
      }
      sizeSelect.value = item.size;
      finishSelect.value = item.finish;
    }
  }

  onChange(event) {
    const tier = event.target.closest("[data-global-tier]")?.querySelector("input:checked")?.value;
    const globalFinish = event.target.closest("[data-global-finish]")?.querySelector("input:checked")?.value;
    const itemElement = event.target.closest("[data-gallery-item]");

    if (tier) {
      for (const item of this.items) {
        item.size = item[`${tier}Size`];
        item.element.querySelector("[data-item-size]").value = item.size;
      }
    } else if (globalFinish) {
      for (const item of this.items) {
        item.finish = globalFinish;
        item.element.querySelector("[data-item-finish]").value = item.finish;
      }
    } else if (itemElement) {
      const item = this.items.find((candidate) => candidate.element === itemElement);
      item.size = itemElement.querySelector("[data-item-size]").value;
      item.finish = itemElement.querySelector("[data-item-finish]").value;
    }
    this.clearStatus();
    this.resolveAll();
  }

  resolveAll() {
    let total = 0;
    let valid = this.items.length > 0;
    for (const item of this.items) {
      const sizeSelect = item.element.querySelector("[data-item-size]");
      const finishSelect = item.element.querySelector("[data-item-finish]");
      for (const option of sizeSelect.options) {
        const candidate = variantFor(item.product, option.value, item.finish);
        option.disabled = !candidate?.available;
      }
      for (const option of finishSelect.options) {
        const candidate = variantFor(item.product, item.size, option.value);
        option.disabled = !candidate?.available;
      }
      item.variant = variantFor(item.product, item.size, item.finish);
      const price = item.element.querySelector("[data-item-price]");
      if (!item.variant || !item.variant.available) {
        valid = false;
        price.textContent = "Unavailable";
        price.setAttribute("data-unavailable", "true");
      } else {
        price.removeAttribute("data-unavailable");
        price.textContent = money(item.variant.price, this.config.currency);
        total += Number(item.variant.price);
      }
    }
    const discountPercentage = Math.min(100, Math.max(0, Number(this.config.discountPercentage) || 0));
    const discountedTotal = this.items.reduce((sum, item) => (
      sum + (item.variant?.available
        ? Math.round(Number(item.variant.price) * (100 - discountPercentage) / 100)
        : 0)
    ), 0);
    const listText = valid ? money(total, this.config.currency) : "Unavailable combination";
    const finalText = valid ? money(discountedTotal, this.config.currency) : "Unavailable combination";
    for (const node of this.listTotals) {
      node.textContent = listText;
      node.hidden = !valid || discountPercentage <= 0;
    }
    for (const node of this.discountedTotals) node.textContent = discountPercentage > 0 ? finalText : listText;
    this.currentListTotal = total;
    this.currentDiscountedTotal = discountedTotal;
    this.button.disabled = !valid;
  }

  setStatus(message, error = false) {
    this.status.textContent = message || "";
    this.status.dataset.error = error ? "true" : "false";
  }

  clearStatus() { this.setStatus(""); }

  setBusy(busy) {
    this.button.disabled = busy;
    this.button.setAttribute("aria-busy", busy ? "true" : "false");
  }

  async refreshProducts() {
    const root = window.Shopify?.routes?.root || "/";
    const freshProducts = await Promise.all(this.items.map(async (item) => {
      const response = await fetch(`${root}products/${item.product.handle}.js`, { headers: { Accept: "application/json" } });
      if (!response.ok) throw new Error(`Could not refresh ${item.product.title}`);
      return response.json();
    }));

    let changed = false;
    freshProducts.forEach((product, index) => {
      const item = this.items[index];
      const previous = item.variant;
      item.product = product;
      const fresh = variantFor(product, item.size, item.finish);
      if (!fresh || !fresh.available || !previous || String(fresh.id) !== String(previous.id) || Number(fresh.price) !== Number(previous.price)) changed = true;
    });
    this.resolveAll();
    return changed;
  }

  cartItems() {
    return buildGalleryCartItems(this.items, this.config, instanceId());
  }

  async addToCart() {
    if (this.button.disabled) return;
    this.setBusy(true);
    this.setStatus("Checking current prices and availability…");

    try {
      const changed = await this.refreshProducts();
      if (changed) {
        this.setStatus("A price or availability changed. We have refreshed the wall — please review it before adding again.", true);
        return;
      }

      const items = this.cartItems();
      const sections = [];
      document.dispatchEvent(new CartGroupedSections(sections));
      const response = await fetch(window.FoxTheme?.routes?.cart_add_url || "/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items, sections: [...new Set(sections)].join(","), sections_url: window.location.pathname }),
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || "The gallery wall could not be added.");

      const analytics = {
        gallery_wall_id: String(this.config.setId),
        gallery_wall_title: this.config.title,
        item_count: items.length,
        value: this.currentDiscountedTotal / 100,
        currency: this.config.currency,
      };
      window.Shopify?.analytics?.publish?.("gallery_wall_add_to_cart", analytics);
      this.dispatchEvent(new CustomEvent("gallery-wall:add-to-cart", { bubbles: true, detail: analytics }));

      if (window.FoxTheme?.settings?.cartType !== "drawer") {
        window.location.assign(window.FoxTheme?.routes?.cart_url || "/cart");
        return;
      }
      this.dispatchEvent(new CartAddEvent(result, this.id, { itemCount: items.length, sections: result.sections }));
      document.dispatchEvent(new CustomEvent(ThemeEvents.productAjaxAdded, { detail: { product: result } }));
      this.setStatus("The complete gallery wall is in your basket.");
    } catch (error) {
      this.dispatchEvent(new CartErrorEvent(this.id, error.message));
      document.dispatchEvent(new CustomEvent(ThemeEvents.productAjaxError, { detail: { errorMessage: error.message } }));
      this.setStatus(error.message || "The gallery wall could not be added. Please try again.", true);
    } finally {
      this.setBusy(false);
      this.resolveAll();
    }
  }
}

if (!customElements.get("solis-gallery-wall")) customElements.define("solis-gallery-wall", SolisGalleryWall);
